import { useEffect, useCallback, useRef } from 'react';
import { X, ArrowLeft, ExternalLink } from 'lucide-react';
import type { WikiPage } from '@/types';

interface WikiPageDrawerProps {
  page: WikiPage;
  onClose: () => void;
  onNext?: () => void;
  onPrev?: () => void;
  onBacklinkClick?: (itemId: string) => void;
  onRelatedPageClick?: (slug: string) => void;
}

function timeAgo(dateStr: string): string {
  const seconds = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (seconds < 60) return 'just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  return `${Math.floor(seconds / 86400)}d ago`;
}

function kindBadgeColor(kind: string): string {
  if (kind === 'entity') return '#58a6ff';
  if (kind === 'concept') return '#a371f7';
  return '#3fb950';
}

export function WikiPageDrawer({
  page,
  onClose,
  onNext,
  onPrev,
  onBacklinkClick,
  onRelatedPageClick,
}: WikiPageDrawerProps) {
  // Keyboard navigation: j/k for next/prev, Escape to close
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'j' && onNext && !e.ctrlKey && !e.metaKey) {
        e.preventDefault();
        onNext();
      } else if (e.key === 'k' && onPrev && !e.ctrlKey && !e.metaKey) {
        e.preventDefault();
        onPrev();
      }
    },
    [onClose, onNext, onPrev]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const drawerRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  // Focus trap: move focus into drawer, trap Tab, restore on close
  useEffect(() => {
    previousFocusRef.current = document.activeElement as HTMLElement | null;
    document.body.style.overflow = 'hidden';

    // Auto-focus the drawer
    const timer = setTimeout(() => {
      drawerRef.current?.focus();
    }, 50);

    return () => {
      clearTimeout(timer);
      document.body.style.overflow = '';
      previousFocusRef.current?.focus();
    };
  }, []);

  // Trap Tab key within the drawer
  useEffect(() => {
    const handleTab = (e: KeyboardEvent) => {
      if (e.key !== 'Tab' || !drawerRef.current) return;
      const focusable = drawerRef.current.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (focusable.length === 0) return;
      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', handleTab);
    return () => document.removeEventListener('keydown', handleTab);
  }, []);

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/20 z-40" onClick={onClose} aria-hidden="true" />

      {/* Drawer */}
      <div
        ref={drawerRef}
        tabIndex={-1}
        className="fixed top-0 right-0 h-full w-full md:w-[480px] bg-[var(--bg-primary)] shadow-xl z-50 flex flex-col overflow-hidden animate-slide-in-right outline-none"
        role="dialog"
        aria-modal="true"
        aria-label={page.title}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--border-primary)]">
          <div className="flex items-center gap-3 min-w-0">
            <button
              onClick={onClose}
              className="p-1 rounded hover:bg-[var(--bg-secondary)] transition-colors"
              aria-label="Close"
            >
              <ArrowLeft className="h-4 w-4" />
            </button>
            <div className="min-w-0">
              <h2 className="font-serif text-lg font-semibold truncate">{page.title}</h2>
              <div className="flex items-center gap-2 mt-0.5">
                <span
                  className="text-xs font-medium px-2 py-0.5 rounded-full"
                  style={{
                    backgroundColor: `${kindBadgeColor(page.kind)}15`,
                    color: kindBadgeColor(page.kind),
                  }}
                >
                  {page.kind}
                </span>
                <span className="text-xs text-[var(--text-tertiary)]">
                  {page.itemCount} {page.itemCount === 1 ? 'item' : 'items'}
                </span>
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded hover:bg-[var(--bg-secondary)] transition-colors"
            aria-label="Close drawer"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {/* Markdown content (rendered as text for now, react-markdown can be added later) */}
          <div className="prose prose-sm max-w-none">
            {page.content.split('\n').map((line, i) => {
              if (line.startsWith('## ')) {
                return (
                  <h3 key={i} className="font-serif text-base font-semibold mt-4 mb-2">
                    {line.replace('## ', '')}
                  </h3>
                );
              }
              if (line.startsWith('### ')) {
                return (
                  <h4 key={i} className="font-medium text-sm mt-3 mb-1">
                    {line.replace('### ', '')}
                  </h4>
                );
              }
              if (line.startsWith('- ')) {
                return (
                  <li key={i} className="text-sm text-[var(--text-primary)] ml-4 list-disc">
                    {line.replace('- ', '')}
                  </li>
                );
              }
              if (line.trim() === '') {
                return <div key={i} className="h-2" />;
              }
              return (
                <p key={i} className="text-sm text-[var(--text-primary)] leading-relaxed">
                  {line}
                </p>
              );
            })}
          </div>

          {/* Backlinks */}
          {page.backlinks.length > 0 && (
            <div className="mt-6 pt-4 border-t border-[var(--border-primary)]">
              <h4 className="font-medium text-sm text-[var(--text-secondary)] mb-2">
                Items ({page.backlinks.length})
              </h4>
              <ul className="space-y-1">
                {page.backlinks.map((itemId) => (
                  <li key={itemId}>
                    <button
                      onClick={() => onBacklinkClick?.(itemId)}
                      className="flex items-center gap-2 w-full text-left px-2 py-1.5 rounded text-sm hover:bg-[var(--bg-secondary)] transition-colors"
                    >
                      <ExternalLink className="h-3 w-3 text-[var(--text-tertiary)] flex-shrink-0" />
                      <span className="truncate text-[var(--text-primary)]">{itemId}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Related Pages */}
          {page.relatedPages.length > 0 && (
            <div className="mt-4 pt-4 border-t border-[var(--border-primary)]">
              <h4 className="font-medium text-sm text-[var(--text-secondary)] mb-2">See Also</h4>
              <ul className="space-y-1">
                {page.relatedPages.map((slug) => (
                  <li key={slug}>
                    <button
                      onClick={() => onRelatedPageClick?.(slug)}
                      className="flex items-center gap-2 w-full text-left px-2 py-1.5 rounded text-sm hover:bg-[var(--bg-secondary)] transition-colors text-[var(--text-primary)]"
                    >
                      <span className="text-[var(--text-tertiary)]">→</span>
                      <span>{slug}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Last synced */}
          <div className="mt-4 pt-4 border-t border-[var(--border-primary)]">
            <p className="text-xs text-[var(--text-tertiary)]">
              Last synced: {timeAgo(page.lastSyncedAt)}
            </p>
          </div>
        </div>
      </div>
    </>
  );
}
